/**
 * Contenido de ayuda contextual para cada panel/sección del Clinical Dashboard.
 * Cada entrada tiene: title, description, y sections (array de bloques explicativos).
 */

export const HELP_CONTENT = {
  // ─── Location Selector ────────────────────────────────────────
  locationSelector: {
    title: "Location Selector",
    description:
      "Select a clinical location to load its patient data. All panels update automatically when you change locations.",
    sections: [
      {
        heading: "How it works",
        items: [
          "Choose from 11 available clinical locations across Florida",
          "The number next to each location shows total assigned patients",
          "Your last selection is remembered for your next visit",
          "All 6 dashboard panels refresh with the selected location's data",
        ],
      },
    ],
  },

  // ─── Panel 1: KPI Overview ────────────────────────────────────
  kpiOverview: {
    title: "KPI Overview",
    description:
      "High-level snapshot of your patient panel. Each card is clickable and navigates to the detailed view.",
    sections: [
      {
        heading: "Segment Cards (Top Row)",
        items: [
          "Assigned — Total patients assigned to this location",
          "High Cost — Patients flagged as high cost utilizers",
          "High Risk — Patients with elevated clinical risk scores",
          "Emergency — Patients with recent ER visits",
          "Discharged — Recently discharged patients requiring follow-up",
          "Admitted — Currently admitted inpatients (requires immediate attention)",
        ],
      },
      {
        heading: "Composite Cards (Bottom Row)",
        items: [
          "Care Gaps — Open quality measures. The progress bar shows open vs closed ratio. 'Non-Compliant' counts patients failing critical measures",
          "Coding Opportunities — HCC coding gaps not yet addressed. Progress bar shows not addressed vs total. Click to review coding worklist",
        ],
      },
      {
        heading: "Clicking a card",
        items: [
          "Segment cards navigate to the Patient Map filtered by that segment",
          "Care Gaps card navigates to the Care Gaps module",
          "Coding card navigates to the Coding Opportunities module",
        ],
      },
    ],
  },

  // ─── Panel 2: Real-Time Alerts ────────────────────────────────
  alerts: {
    title: "Real-Time Alerts (ADT)",
    description:
      "Live feed of Admission, Discharge, and Transfer events. These patients need timely follow-up to prevent readmissions and complications.",
    sections: [
      {
        heading: "Columns",
        items: [
          "Admitted (red) — Currently admitted inpatients. Pulsing badge indicates active admissions requiring immediate review",
          "Discharged (amber) — Recently discharged patients. Review for follow-up scheduling and transition-of-care needs",
          "Emergency (orange) — Patients with recent ER visits. Evaluate for preventable ER patterns",
        ],
      },
      {
        heading: "Patient Card Details",
        items: [
          "Facility — Hospital or ER where the event occurred",
          "Admit/Discharge dates — When the event started/ended",
          "Days badge — Days since admission (red if >7 days)",
          "LACE Score — Readmission risk: Green (0-9) Low, Amber (10-15) Moderate, Red (16+) High",
          "BH flag (brain icon) — Indicates behavioral health diagnosis",
          "Disposition — Discharge outcome (e.g., 'Expired', 'Home', 'SNF')",
          "Dx — Primary diagnoses (ICD-10 codes)",
        ],
      },
      {
        heading: "Actions",
        items: [
          "Click a patient card to open their Clinical Info page",
          "'View All' shows the complete list in Patient Map",
        ],
      },
    ],
  },

  // ─── Panel 3: My Patients Worklist ────────────────────────────
  myPatients: {
    title: "My Patients Worklist",
    description:
      "Your complete patient panel sorted by clinical priority (triage order). The most urgent patients appear at the top automatically.",
    sections: [
      {
        heading: "Triage Sort Order (automatic)",
        items: [
          "1. Currently admitted patients (highest priority)",
          "2. Recently discharged patients",
          "3. Health severity: End of Life > Complex > At Risk > Chronic > Acute > Well",
          "4. Time since last visit: longest gaps first",
          "5. Open care gaps: most gaps first",
          "6. Alphabetical by name (tie-breaker)",
        ],
      },
      {
        heading: "Columns",
        items: [
          "Patient — Name (click to open profile). Inline flags show special conditions",
          "Age — Patient's current age",
          "Status — Health status chip: Well (green), Acute (yellow), Chronic (blue), At Risk (orange), Complex (red), End of Life (purple)",
          "Last Visit — Time since last appointment: <3 Mo (dark green), 3 Mo (green), 6 Mo (amber), 9 Mo (orange), 12+ Mo (red = critical)",
          "Gaps — Open care gap count. Red badge if 3+, amber if 1-2",
          "Conditions — Primary diagnoses (hover for full list)",
          "ER — Emergency room visit count. Red badge if any",
          "Meds — Medication count. Amber highlight if 5+ (polypharmacy)",
          "Provider — Assigned PCP name",
          "Open — Click to view full Clinical Info page",
        ],
      },
      {
        heading: "Patient Flags (icons next to name)",
        items: [
          "Bed icon — Currently admitted inpatient",
          "Arrows icon — Recently discharged",
          "Red dot — High risk (LACE score 16+)",
          "Pill icon — Polypharmacy (5+ medications)",
          "Lightning icon — Clinical trigger active",
          "NEW badge — Newly assigned member",
        ],
      },
      {
        heading: "Row Colors",
        items: [
          "Red left border — Complex or End of Life patients",
          "Orange left border — At Risk patients",
          "No border — Other statuses",
        ],
      },
      {
        heading: "Filters",
        items: [
          "Search — Filter by patient name or CIP ID",
          "Status — Filter by health status category",
          "Last Visit — Filter by visit timeframe",
          "Provider — Filter by assigned PCP",
        ],
      },
    ],
  },

  // ─── Panel 4: Insights ────────────────────────────────────────
  insights: {
    title: "Insights",
    description:
      "Three analytical views of your patient panel: quality measures, coding opportunities, and visit recall data.",
    sections: [
      {
        heading: "Tab: Care Gaps",
        items: [
          "Donut ring shows the percentage of open gaps vs total",
          "Open count — Gaps that need to be closed (action needed)",
          "Closed count — Successfully completed measures",
          "Non-Compliant — Patients failing critical quality measures (urgent)",
          "Compliance bars show each measure's completion rate: Red (<30%), Amber (30-60%), Blue (60-85%), Green (>85%)",
          "'Work Care Gaps' navigates to the Care Gaps module",
        ],
      },
      {
        heading: "Tab: Coding",
        items: [
          "Not Addressed — HCC coding opportunities pending review",
          "Total — All identified coding opportunities",
          "Addressed — Already reviewed and resolved",
          "Condition Types: Dropped (previously documented, not recaptured), Suspect (new potential conditions), Pharmacy (medication-implied conditions)",
          "Top HCCs — Most common unaddressed condition categories",
          "'Review Coding Opps' navigates to the Coding module",
        ],
      },
      {
        heading: "Tab: No Visit",
        items: [
          "Distribution cards show patient counts by time since last visit",
          "Percentage stat highlights patients without a visit in 12+ months (critical recall need)",
          "Contactability shows how many patients have phone/email on file for outreach",
          "Recall eligible — Total patients who should be contacted for scheduling",
        ],
      },
    ],
  },

  // ─── Panel 5: Recall ──────────────────────────────────────────
  recall: {
    title: "Patient Recall",
    description:
      "Patients who haven't visited in an extended period and need to be contacted for appointment scheduling.",
    sections: [
      {
        heading: "Stats Cards",
        items: [
          "12+ Months (red) — Most critical: haven't visited in over a year",
          "9 Months (orange) — Need scheduling soon",
          "6 Months (amber) — Approaching recall threshold",
          "With Phone (green) — Contactable by phone for outreach",
        ],
      },
      {
        heading: "Table Columns",
        items: [
          "Patient — Name (click to open profile)",
          "Since — Time since last visit, color-coded by urgency",
          "Status — Health status chip",
          "Gaps — Open care gaps count",
          "Phone — Clickable phone link (opens dialer)",
          "Email — Clickable email link (opens email client)",
          "Provider — Assigned PCP",
          "Create Task — Opens task form pre-filled with patient info for recall follow-up",
        ],
      },
    ],
  },

  // ─── Panel 6: Tasks ───────────────────────────────────────────
  tasks: {
    title: "My Tasks",
    description:
      "Personal task manager for clinical follow-ups. Tasks are saved locally and persist between sessions.",
    sections: [
      {
        heading: "Stats",
        items: [
          "Due Today (amber) — Tasks due for completion today",
          "Overdue (red) — Tasks past their due date",
          "Done Today (green) — Tasks completed today",
        ],
      },
      {
        heading: "Task Groups",
        items: [
          "Overdue — Red background, these need immediate attention",
          "Due Today — Tasks scheduled for today",
          "Upcoming — Future tasks",
          "Completed — Recently finished tasks (last 5 shown)",
        ],
      },
      {
        heading: "Task Item",
        items: [
          "Checkbox — Mark task as completed (adds strikethrough)",
          "Color dot — Priority indicator: Red (high), Amber (medium), Green (low)",
          "Category chip — Task type (Follow-Up, Care Gap, Coding, Recall, etc.)",
          "Due date — Shows 'Today' or the date. Red text if overdue",
          "Three-dot menu — Edit, Cancel, or Delete the task",
        ],
      },
      {
        heading: "Creating Tasks",
        items: [
          "'+ New Task' opens the task creation form",
          "Tasks can also be created from: Recall table ('Create Task'), Alert cards, and Insights panels",
          "Each task can be linked to a specific patient",
          "9 categories available: Follow-Up, Care Gap, Coding Review, Outreach Call, Lab Review, Referral, Documentation, Patient Recall, Other",
          "3 priority levels: High, Medium, Low",
        ],
      },
    ],
  },
};
